import { useState, useEffect } from "react";
import { CheckCircle2, XCircle, Loader2, RefreshCw, Send, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

function fallbackCopy(text) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  } catch {}
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({});
  const [checking, setChecking] = useState(false);
  const [tg, setTg] = useState(null);
  const [tgLoading, setTgLoading] = useState(false);
  const [tgBusy, setTgBusy] = useState("");
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetStep, setResetStep] = useState(0); // 0 fechado, 1 primeira confirmação, 2 confirmação final

  const loadSettings = () => api.get("/admin/settings").then(({ data }) => { setSettings(data); setForm({}); }).catch(() => {});
  const checkProvider = () => { setChecking(true); api.get("/admin/provider/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ connected: false })).finally(() => setChecking(false)); };
  const loadTg = () => { setTgLoading(true); api.get("/telegram/webhook-status").then(({ data }) => setTg(data)).catch(() => setTg(null)).finally(() => setTgLoading(false)); };

  useEffect(() => { loadSettings(); checkProvider(); loadTg(); }, []);

  const setWebhook = async () => {
    setTgBusy("set");
    try { await api.post("/telegram/set-webhook"); toast.success("Webhook vinculado a esta implantação"); loadTg(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setTgBusy(""); }
  };
  const removeWebhook = async () => {
    setTgBusy("del");
    try { await api.post("/telegram/delete-webhook"); toast.success("Webhook removido (bot volta ao polling)"); loadTg(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setTgBusy(""); }
  };

  const doReset = async () => {
    setResetStep(0);
    setResetting(true);
    try {
      const { data } = await api.post("/admin/factory-reset", { confirm: resetPhrase });
      const total = Object.values(data.removed || {}).reduce((a, b) => a + b, 0);
      toast.success(`Limpeza concluída: ${total} registros removidos. Só o admin permaneceu.`);
      setResetPhrase("");
      loadSettings();
    } catch (e) { toast.error(apiError(e)); }
    finally { setResetting(false); }
  };

  const save = async () => {
    try {
      const payload = { ...form };
      if (payload.reseller_unlock_credits === "" || Number.isNaN(payload.reseller_unlock_credits)) delete payload.reseller_unlock_credits;
      await api.put("/admin/settings", payload);
      toast.success("Configurações salvas");
      loadSettings();
      checkProvider();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!settings) return <div className="text-muted-foreground">Carregando...</div>;

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-settings">
      <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Configurações</h2>

      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold">Integração Ative Apps</h3>
          <div className="flex items-center gap-2">
            {status && (status.connected
              ? <Badge className="gap-1 bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30" variant="outline" data-testid="provider-connected"><CheckCircle2 className="h-3 w-3" /> Conectado</Badge>
              : <Badge variant="destructive" className="gap-1" data-testid="provider-disconnected"><XCircle className="h-3 w-3" /> Desconectado</Badge>)}
            <Button variant="ghost" size="icon" onClick={checkProvider} data-testid="provider-refresh-btn">{checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}</Button>
          </div>
        </div>
        {status?.connected && <p className="mt-2 text-sm text-muted-foreground">Créditos no fornecedor: <span className="font-medium text-foreground">{status.provider_credits}</span></p>}
        <div className="mt-4 space-y-3">
          <div className="space-y-2">
            <Label>Chave da API (X-API-Key)</Label>
            <Input placeholder={settings.ativeapp_api_key_masked || "ak_live_..."} value={form.ativeapp_api_key || ""} onChange={(e) => setForm({ ...form, ativeapp_api_key: e.target.value })} data-testid="settings-apikey-input" />
            <p className="text-xs text-muted-foreground">Atual: {settings.ativeapp_api_key_masked}. Deixe em branco para manter.</p>
          </div>
          <div className="space-y-2">
            <Label>URL base da API</Label>
            <Input placeholder="https://api.ativeapp.com/api" value={form.ativeapp_base_url ?? (settings.ativeapp_base_url || "")} onChange={(e) => setForm({ ...form, ativeapp_base_url: e.target.value })} data-testid="settings-baseurl-input" />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="font-heading font-semibold">Plataforma & Suporte</h3>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2"><Label>Nome da plataforma</Label><Input placeholder={settings.platform_name || "VULPIX"} value={form.platform_name ?? (settings.platform_name || "")} onChange={(e) => setForm({ ...form, platform_name: e.target.value })} data-testid="settings-name-input" /></div>
          <div className="space-y-2"><Label>E-mail de suporte</Label><Input value={form.support_email ?? (settings.support_email || "")} onChange={(e) => setForm({ ...form, support_email: e.target.value })} data-testid="settings-email-input" /></div>
          <div className="space-y-2"><Label>WhatsApp</Label><Input value={form.support_whatsapp ?? (settings.support_whatsapp || "")} onChange={(e) => setForm({ ...form, support_whatsapp: e.target.value })} data-testid="settings-whatsapp-input" /></div>
          <div className="space-y-2"><Label>Telegram</Label><Input value={form.support_telegram ?? (settings.support_telegram || "")} onChange={(e) => setForm({ ...form, support_telegram: e.target.value })} data-testid="settings-telegram-input" /></div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="font-heading font-semibold">Webhook de Ativações</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Cole esta URL no painel do Ative App (seção "Webhook de Ativações"). Enviaremos e receberemos as
          mudanças de status das ativações em tempo real e atualizaremos os créditos automaticamente.
        </p>
        <div className="mt-4 space-y-2">
          <Label>URL do Webhook (cole no Ative App)</Label>
          <div className="flex gap-2">
            <Input readOnly value={settings.webhook_url || ""} data-testid="settings-webhook-url" className="font-mono text-xs" />
            <Button
              variant="outline"
              onClick={() => {
                const url = settings.webhook_url || "";
                const done = () => toast.success("URL copiada!");
                try {
                  if (navigator.clipboard?.writeText) {
                    navigator.clipboard.writeText(url).then(done).catch(() => { fallbackCopy(url); done(); });
                  } else { fallbackCopy(url); done(); }
                } catch { fallbackCopy(url); done(); }
              }}
              data-testid="settings-webhook-copy"
            >
              Copiar
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Mantenha esta URL secreta — ela contém um token de segurança.</p>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="font-heading font-semibold">Produção & Integrações (Bot + Lynx PIX)</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Defina a URL pública de produção para onde os webhooks devem responder. O bot e o site
          compartilham os mesmos dados; aponte tudo para o mesmo endereço.
        </p>
        <div className="mt-4 space-y-3">
          <div className="space-y-2">
            <Label>URL base pública (produção)</Label>
            <Input placeholder="https://seu-dominio.com" value={form.public_base_url ?? (settings.public_base_url || "")} onChange={(e) => setForm({ ...form, public_base_url: e.target.value })} data-testid="settings-baseurl-public" />
            <p className="text-xs text-muted-foreground">Usada para montar as URLs de webhook abaixo. Em branco = ambiente atual.</p>
          </div>
          <div className="space-y-2">
            <Label>URL do Webhook Lynx PIX (cole no painel Lynx → API → Webhooks)</Label>
            <div className="flex gap-2">
              <Input readOnly value={settings.lynx_webhook_url || ""} data-testid="settings-lynx-webhook-url" className="font-mono text-xs" />
              <Button variant="outline" onClick={() => { const u = settings.lynx_webhook_url || ""; try { navigator.clipboard?.writeText ? navigator.clipboard.writeText(u).then(() => toast.success("URL copiada!")).catch(() => { fallbackCopy(u); toast.success("URL copiada!"); }) : (fallbackCopy(u), toast.success("URL copiada!")); } catch { fallbackCopy(u); toast.success("URL copiada!"); } }} data-testid="settings-lynx-webhook-copy">Copiar</Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Segredo do Webhook Lynx (whsec_…)</Label>
            <Input placeholder={settings.lynx_webhook_secret_set ? "•••••• (configurado)" : "whsec_..."} value={form.lynx_webhook_secret || ""} onChange={(e) => setForm({ ...form, lynx_webhook_secret: e.target.value })} data-testid="settings-lynx-secret" />
            <p className="text-xs text-muted-foreground">{settings.lynx_webhook_secret_set ? "Configurado. Deixe em branco para manter." : "Cole o segredo gerado no painel Lynx para validar a assinatura."}</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2"><Label>Link de suporte do Bot</Label><Input placeholder="https://t.me/seu_suporte" value={form.bot_support_link ?? (settings.bot_support_link || "")} onChange={(e) => setForm({ ...form, bot_support_link: e.target.value })} data-testid="settings-bot-support" /></div>
            <div className="space-y-2"><Label>Canal/Grupo da comunidade (gate)</Label><Input placeholder="@seu_canal" value={form.bot_community_chat ?? (settings.bot_community_chat || "")} onChange={(e) => setForm({ ...form, bot_community_chat: e.target.value })} data-testid="settings-bot-community" /><p className="text-xs text-muted-foreground">Ex.: @vulpixativacoes. O bot precisa ser <b>ADMINISTRADOR</b> deste canal para verificar quem entrou.</p></div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 p-4">
            <div className="pr-4">
              <Label className="text-sm">Portão da Comunidade</Label>
              <p className="text-xs text-muted-foreground">Quando ligado, só quem já entrou no canal acima consegue usar o bot (comandos, botões e mensagens).</p>
            </div>
            <Switch checked={form.bot_community_gate ?? !!settings.bot_community_gate} onCheckedChange={(v) => setForm({ ...form, bot_community_gate: v })} data-testid="settings-bot-community-gate" />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="font-heading font-semibold">Revenda (auto-serviço)</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Permite o próprio usuário virar revendedor (loja + rede) ao atingir um limite de créditos.
          É só um limite — os créditos <b>não são debitados</b> e o desbloqueio é <b>permanente</b>.
        </p>
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 p-4">
            <div className="pr-4">
              <Label className="text-sm">Permitir auto-habilitação de revenda</Label>
              <p className="text-xs text-muted-foreground">Quando ligado, aparece um card no painel do usuário convidando-o a virar revendedor.</p>
            </div>
            <Switch checked={form.reseller_self_signup ?? (settings.reseller_self_signup ?? true)} onCheckedChange={(v) => setForm({ ...form, reseller_self_signup: v })} data-testid="settings-reseller-selfsignup" />
          </div>
          <div className="max-w-xs space-y-2">
            <Label>Créditos para desbloquear</Label>
            <Input type="number" min="1" placeholder="30" value={form.reseller_unlock_credits ?? (settings.reseller_unlock_credits ?? 30)} onChange={(e) => { const n = parseInt(e.target.value, 10); setForm({ ...form, reseller_unlock_credits: Number.isNaN(n) ? "" : n }); }} data-testid="settings-reseller-threshold" />
            <p className="text-xs text-muted-foreground">Padrão: 30. O usuário desbloqueia ao ter esse total de créditos (1ª recarga ou acúmulo).</p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold">Telegram (Produção)</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Registre o webhook do bot nesta implantação para operar em produção sem depender do worker de polling.
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={loadTg} data-testid="tg-refresh-btn">
            {tgLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>

        <div className="mt-4 space-y-2 rounded-lg border border-border/60 bg-muted/30 p-4" data-testid="tg-status">
          <StatusRow label="Token do bot" ok={tg?.token_ok} />
          <StatusRow label="Webhook registrado" ok={!!tg?.webhook_url} />
          <div className="flex items-center justify-between py-1 text-sm">
            <span className="text-muted-foreground">Bot</span>
            <span className="font-mono font-medium" data-testid="tg-bot-username">{tg?.bot_username ? `@${tg.bot_username}` : "—"}</span>
          </div>
          <StatusRow label="Apontando p/ esta implantação" ok={tg?.points_here} />
          {tg?.pending > 0 && (
            <div className="flex items-center justify-between py-1 text-sm">
              <span className="text-muted-foreground">Updates pendentes</span>
              <span className="font-medium text-amber-500">{tg.pending}</span>
            </div>
          )}
          {tg?.last_error && <p className="pt-1 text-xs text-destructive">Último erro: {tg.last_error}</p>}
          {(tg?.webhook_url || tg?.expected_url) && (
            <p className="break-all pt-2 text-xs text-muted-foreground" data-testid="tg-webhook-url">
              {tg?.webhook_url || tg?.expected_url}
            </p>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={setWebhook} disabled={!!tgBusy} data-testid="tg-set-webhook-btn" className="gap-2">
            {tgBusy === "set" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {tg?.points_here ? "Re-vincular webhook" : "Vincular webhook (produção)"}
          </Button>
          {tg?.webhook_url && (
            <Button variant="outline" onClick={removeWebhook} disabled={!!tgBusy} data-testid="tg-delete-webhook-btn">
              {tgBusy === "del" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remover webhook (voltar ao polling)"}
            </Button>
          )}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Usa a "URL base pública (produção)" configurada acima. Ao vincular o webhook, todas as mensagens do bot
          passam a ser processadas por esta implantação.
        </p>
      </div>

      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-5" data-testid="admin-danger-zone">
        <h3 className="font-heading font-semibold text-destructive">Zona de perigo — Limpar dados</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Apaga <b>todos os dados de teste</b> (usuários não-admin, ativações, pedidos, financeiro,
          tickets, logs e cupons). Mantém <b>apenas a sua conta admin</b>, o catálogo de apps, as
          configurações e os pacotes. Use isto <b>em produção</b> para começar do zero. Ação irreversível.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">Para sua segurança, pediremos <b>2 confirmações</b> antes de apagar.</p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Input placeholder="Digite: LIMPAR TUDO" value={resetPhrase} onChange={(e) => setResetPhrase(e.target.value)} data-testid="danger-reset-input" />
          <Button variant="destructive" disabled={resetPhrase !== "LIMPAR TUDO" || resetting} onClick={() => setResetStep(1)} data-testid="danger-reset-btn" className="shrink-0 gap-2">
            {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />} Limpar dados de teste
          </Button>
        </div>
      </div>

      {/* 1ª confirmação */}
      <AlertDialog open={resetStep === 1} onOpenChange={(o) => { if (!o) setResetStep((s) => (s === 1 ? 0 : s)); }}>
        <AlertDialogContent data-testid="danger-reset-dialog-1">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmação 1 de 2 — Limpar todos os dados?</AlertDialogTitle>
            <AlertDialogDescription>
              Isto apaga permanentemente usuários (exceto o admin), ativações, pedidos, financeiro,
              tickets, logs e cupons. <b>Mantém apenas a sua conta admin</b>, o catálogo, as configurações
              e os pacotes. Esta ação <b>não pode ser desfeita</b>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="danger-reset-cancel-1">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => setResetStep(2)} data-testid="danger-reset-next-1">Continuar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 2ª confirmação (final) */}
      <AlertDialog open={resetStep === 2} onOpenChange={(o) => { if (!o) setResetStep((s) => (s === 2 ? 0 : s)); }}>
        <AlertDialogContent data-testid="danger-reset-dialog-2">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Confirmação 2 de 2 — Última chance</AlertDialogTitle>
            <AlertDialogDescription>
              Tem <b>certeza absoluta</b>? Ao confirmar, todos os dados serão apagados imediatamente e
              somente a sua conta admin permanecerá. Não há como reverter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="danger-reset-cancel-2">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doReset} data-testid="danger-reset-confirm-2" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Sim, apagar tudo agora
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Button onClick={save} data-testid="settings-save-btn">Salvar configurações</Button>
    </div>
  );
}

function StatusRow({ label, ok }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {ok
        ? <span className="inline-flex items-center gap-1 font-medium text-green-600 dark:text-green-400"><CheckCircle2 className="h-4 w-4" /> OK</span>
        : <span className="inline-flex items-center gap-1 font-medium text-muted-foreground"><XCircle className="h-4 w-4" /> —</span>}
    </div>
  );
}
